import { useState } from 'react';
import {
  Container,
  Group,
  Title,
  Table,
  Badge,
  Button,
  Modal,
  Stack,
  Text,
  Skeleton,
  ScrollArea,
  Alert,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconEye, IconPhone, IconAlertCircle } from '@tabler/icons-react';
import {
  useVoicemailOrders,
  useVoicemailDetail,
  useVoicemailCallbacks,
  useResolveCallback,
} from '@/hooks/usePortalApi';

const ORDER_DETECTED_COLORS: Record<string, string> = {
  yes: 'green',
  no: 'red',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'yellow',
  processed: 'green',
  failed: 'red',
  callback_required: 'orange',
};

export default function VoicemailOrders() {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: voicemails, isLoading: voicemailsLoading } = useVoicemailOrders();
  const { data: detail } = useVoicemailDetail(selectedId);
  const { data: callbacks, isLoading: callbacksLoading } = useVoicemailCallbacks();
  const resolveCallbackMutation = useResolveCallback();

  const handleView = (id: number) => {
    setSelectedId(id);
    open();
  };

  const handleResolveCallback = async (callbackId: number) => {
    try {
      await resolveCallbackMutation.mutateAsync(callbackId);
      notifications.show({
        title: 'Callback Resolved',
        message: 'Callback has been marked as resolved',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Failed',
        message: 'Failed to resolve callback',
        color: 'red',
      });
    }
  };

  const truncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Voicemail Orders</Title>
      </Group>

      {!callbacksLoading && callbacks && callbacks.length > 0 && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Pending Callbacks"
          color="orange"
          mb="md"
        >
          <Stack gap="xs">
            {callbacks.map((callback: any) => (
              <Group key={callback.id} justify="space-between">
                <Text size="sm">
                  {callback.caller} - {callback.reason}
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPhone size={14} />}
                  onClick={() => handleResolveCallback(callback.id)}
                  loading={resolveCallbackMutation.isPending}
                >
                  Resolve
                </Button>
              </Group>
            ))}
          </Stack>
        </Alert>
      )}

      {voicemailsLoading ? (
        <Stack gap="xs">
          <Skeleton height={50} />
          <Skeleton height={50} />
          <Skeleton height={50} />
        </Stack>
      ) : !voicemails || voicemails.length === 0 ? (
        <Text ta="center" c="dimmed" py="xl">
          No voicemail orders found
        </Text>
      ) : (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Caller</Table.Th>
              <Table.Th>Transcript Preview</Table.Th>
              <Table.Th>Order Detected</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {voicemails.map((vm: any) => (
              <Table.Tr key={vm.id}>
                <Table.Td>
                  {new Date(vm.created_at).toLocaleDateString()}
                </Table.Td>
                <Table.Td>{vm.caller}</Table.Td>
                <Table.Td>{truncate(vm.transcript, 80)}</Table.Td>
                <Table.Td>
                  <Badge
                    color={ORDER_DETECTED_COLORS[vm.order_detected ? 'yes' : 'no']}
                    variant="light"
                  >
                    {vm.order_detected ? 'Yes' : 'No'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLORS[vm.status]} variant="light">
                    {vm.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconEye size={14} />}
                    onClick={() => handleView(vm.id)}
                  >
                    View
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={opened} onClose={close} title="Voicemail Details" size="lg">
        {detail && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Caller
              </Text>
              <Text fw={600}>{detail.caller}</Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Matched Customer
              </Text>
              <Text fw={600}>{detail.matched_customer || 'Unknown'}</Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Confidence Score
              </Text>
              <Text fw={600}>{detail.confidence_score}%</Text>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Callback Status
              </Text>
              <Badge color={STATUS_COLORS[detail.callback_status]} variant="light">
                {detail.callback_status}
              </Badge>
            </Group>

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Full Transcript
              </Text>
              <ScrollArea h={150}>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {detail.transcript}
                </Text>
              </ScrollArea>
            </Stack>

            {detail.detected_items && detail.detected_items.length > 0 && (
              <Stack gap="xs">
                <Text size="sm" fw={600}>
                  Detected Items
                </Text>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Product</Table.Th>
                      <Table.Th>Qty</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {detail.detected_items.map((item: any, idx: number) => (
                      <Table.Tr key={idx}>
                        <Table.Td>{item.product}</Table.Td>
                        <Table.Td>{item.quantity}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            )}
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
